import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, ShoppingCart, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { purchaseOrdersApi, vendorsApi, itemsApi } from '../api/api';

const today = () => new Date().toISOString().split('T')[0];
const EMPTY_FORM = { po_number:'', vendor_id:'', order_date: today(), expected_date:'', notes:'', status:'pending' };
const EMPTY_LINE = { item_id:'', quantity:1, unit_price:0 };

function LineItemsEditor({ items, lines, setLines }) {
  const addLine = () => setLines(l => [...l, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines(l => l.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLines(l => l.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln));

  const total = lines.reduce((s, l) => s + (Number(l.quantity) * Number(l.unit_price)), 0);

  return (
    <div>
      <div className="section-label">Line Items</div>
      <table className="line-items-table">
        <thead><tr>
          <th style={{width:'35%'}}>Item</th>
          <th style={{width:'15%'}}>Qty</th>
          <th style={{width:'20%'}}>Unit Price</th>
          <th style={{width:'20%'}}>Total</th>
          <th style={{width:'10%'}}></th>
        </tr></thead>
        <tbody>
          {lines.map((ln, i) => {
            const item = items.find(it => String(it.id) === String(ln.item_id));
            return (
              <tr key={i}>
                <td>
                  <select value={ln.item_id} onChange={e => {
                    const it = items.find(x => String(x.id) === e.target.value);
                    setLine(i, 'item_id', e.target.value);
                    if (it) setLine(i, 'unit_price', it.unit_price);
                  }}>
                    <option value="">-- Select Item --</option>
                    {items.map(it => <option key={it.id} value={it.id}>{it.item_code} — {it.name}</option>)}
                  </select>
                </td>
                <td><input type="number" min="1" value={ln.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} /></td>
                <td><input type="number" min="0" step="0.01" value={ln.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} /></td>
                <td style={{textAlign:'right', fontFamily:'var(--mono)', fontSize:12}}>
                  ₹{(Number(ln.quantity) * Number(ln.unit_price)).toLocaleString('en-IN')}
                  {item && <div style={{fontSize:10, color:'var(--gray-400)'}}>Stock: {item.stock_qty} {item.unit}</div>}
                </td>
                <td><button type="button" className="btn-icon danger" onClick={() => removeLine(i)}><Trash2 size={12} /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10}}>
        <button type="button" className="btn btn-sm" onClick={addLine}><Plus size={12} /> Add Line</button>
        <div style={{fontFamily:'var(--mono)', fontWeight:600, fontSize:14}}>
          Total: ₹{total.toLocaleString('en-IN', {minimumFractionDigits:2})}
        </div>
      </div>
    </div>
  );
}

function ViewModal({ poId, onClose }) {
  const { data: po, isLoading } = useQuery({ queryKey: ['po', poId], queryFn: () => purchaseOrdersApi.getOne(poId) });
  if (isLoading) return (
    <div className="modal-overlay"><div className="modal modal-lg"><div className="modal-body"><div className="spinner" /></div></div></div>
  );
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>Purchase Order — {po.po_number}</h2>
          <span className={`badge badge-${po.status}`}>{po.status}</span>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-grid" style={{marginBottom:16}}>
            <div><div className="form-label">Vendor</div><strong>{po.vendor_name}</strong></div>
            <div><div className="form-label">Vendor Code</div>{po.vendor_code}</div>
            <div><div className="form-label">Order Date</div>{new Date(po.order_date).toLocaleDateString('en-IN')}</div>
            <div><div className="form-label">Expected Date</div>{po.expected_date ? new Date(po.expected_date).toLocaleDateString('en-IN') : '—'}</div>
          </div>
          <div className="section-label">Items</div>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
            <thead><tr style={{background:'var(--gray-50)'}}>
              <th style={{padding:'8px 12px', textAlign:'left', fontSize:11, color:'var(--gray-400)', textTransform:'uppercase'}}>Item</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Qty</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Unit</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Price</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Total</th>
            </tr></thead>
            <tbody>
              {(po.items||[]).map((it, i) => (
                <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'8px 12px'}}>{it.item_code} — {it.item_name}</td>
                  <td style={{padding:'8px 12px', textAlign:'right'}}>{it.quantity}</td>
                  <td style={{padding:'8px 12px', textAlign:'right'}}>{it.unit}</td>
                  <td style={{padding:'8px 12px', textAlign:'right', fontFamily:'var(--mono)'}}>₹{Number(it.unit_price).toLocaleString('en-IN')}</td>
                  <td style={{padding:'8px 12px', textAlign:'right', fontFamily:'var(--mono)'}}>₹{Number(it.total_price).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td colSpan={4} style={{padding:'10px 12px', textAlign:'right', fontWeight:600}}>Grand Total</td>
              <td style={{padding:'10px 12px', textAlign:'right', fontFamily:'var(--mono)', fontWeight:600}}>₹{Number(po.total_amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
            </tr></tfoot>
          </table>
          {po.notes && <div style={{marginTop:14}}><div className="form-label">Notes</div><p style={{fontSize:13}}>{po.notes}</p></div>}
        </div>
        <div className="modal-footer"><button className="btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

export default function PurchaseOrders() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lines, setLines] = useState([{...EMPTY_LINE}]);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['purchase-orders', search, statusFilter],
    queryFn: () => purchaseOrdersApi.getAll({ search, status: statusFilter }),
  });
  const { data: vendors = [] } = useQuery({ queryKey: ['vendors'], queryFn: () => vendorsApi.getAll({}) });
  const { data: items = [] } = useQuery({ queryKey: ['items-all'], queryFn: () => itemsApi.getAll({ status: 'active' }) });

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? purchaseOrdersApi.update(editing.id, d) : purchaseOrdersApi.create(d),
    onSuccess: () => { qc.invalidateQueries(['purchase-orders']); qc.invalidateQueries(['dashboard']); qc.invalidateQueries(['items']); toast.success(editing ? 'PO updated' : 'PO created'); closeModal(); },
    onError: (e) => toast.error(e.error || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: purchaseOrdersApi.delete,
    onSuccess: () => { qc.invalidateQueries(['purchase-orders']); toast.success('PO deleted'); },
    onError: (e) => toast.error(e.error || 'Failed to delete'),
  });

  const openAdd = async () => {
    const res = await purchaseOrdersApi.nextNumber();
    setEditing(null); setForm({...EMPTY_FORM, po_number: res.po_number}); setLines([{...EMPTY_LINE}]); setModal(true);
  };
  const openEdit = async (po) => {
    const full = await purchaseOrdersApi.getOne(po.id);
    setEditing(po);
    setForm({ po_number: full.po_number, vendor_id: full.vendor_id, order_date: full.order_date?.split('T')[0]||today(), expected_date: full.expected_date?.split('T')[0]||'', notes: full.notes||'', status: full.status });
    setLines((full.items||[]).map(it => ({ item_id: it.item_id, quantity: it.quantity, unit_price: it.unit_price })));
    setModal(true);
  };
  const closeModal = () => { setModal(false); setEditing(null); };
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const submit = (e) => {
    e.preventDefault();
    if (!lines.length || lines.some(l => !l.item_id)) return toast.error('Add at least one valid line item');
    saveMutation.mutate({ ...form, items: lines });
  };

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">Purchase Orders</span>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> New PO</button>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <ShoppingCart size={15} />
            <span className="card-title">Purchase Orders ({orders.length})</span>
            <div className="search-wrap">
              <Search /><input placeholder="Search PO number, vendor..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              {/* <option value="received">Received</option>*/}
              <option value="cancelled">Cancelled</option> 
            </select>
          </div>
          <div className="table-wrap">
            {isLoading ? <div className="spinner" /> : (
              <table>
                <thead><tr><th>PO Number</th><th>Vendor</th><th>Order Date</th><th>Expected Date</th><th>Total Amount</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {orders.length === 0 && <tr><td colSpan={7}><div className="empty-state"><ShoppingCart /><p>No purchase orders found</p></div></td></tr>}
                  {orders.map(po => (
                    <tr key={po.id}>
                      <td className="mono">{po.po_number}</td>
                      <td style={{fontWeight:500}}>{po.vendor_name}</td>
                      <td>{new Date(po.order_date).toLocaleDateString('en-IN')}</td>
                      <td>{po.expected_date ? new Date(po.expected_date).toLocaleDateString('en-IN') : '—'}</td>
                      <td className="amount">₹{Number(po.total_amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                      <td><span className={`badge badge-${po.status}`}>{po.status}</span></td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button className="btn-icon" title="View" onClick={() => setViewId(po.id)}><Eye /></button>
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(po)}><Pencil /></button>
                          {/* <button className="btn-icon danger" onClick={() => { if(window.confirm('Delete this PO?')) deleteMutation.mutate(po.id); }}><Trash2 /></button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {viewId && <ViewModal poId={viewId} onClose={() => setViewId(null)} />}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{editing ? 'Edit Purchase Order' : 'New Purchase Order'}</h2>
              <button className="btn btn-sm" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">PO Number *</label>
                    <input className="form-control" required value={form.po_number} onChange={e => set('po_number', e.target.value)} disabled={!!editing} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vendor *</label>
                    <select className="form-control" required value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}>
                      <option value="">-- Select Vendor --</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_code} — {v.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Order Date *</label>
                    <input className="form-control" type="date" required value={form.order_date} onChange={e => set('order_date', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Expected Delivery Date</label>
                    <input className="form-control" type="date" value={form.expected_date} onChange={e => set('expected_date', e.target.value)} />
                  </div>
                  {editing && (
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        {/* <option value="received">Received (updates stock)</option> */}
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group form-full">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
                  </div>
                </div>
                <LineItemsEditor items={items} lines={lines} setLines={setLines} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : editing ? 'Update PO' : 'Create PO'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}